import ConnectAccountInstitutionListView from '@/components/financialEntities/ConnectAccountInstitutionListView';
import usePlaid from '@/components/plaid/usePlaid';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { showConfetti } from '@/util/confetti';
import { assert } from '@common/errors';
import {
    ASSET_TYPES,
    ExternalFinancialEntityType,
} from '@common/financialEntities';
import { financialCategoryConfigs } from '@common/financialEntities/config';
import { UsePlaidOptions } from '@common/react/aggregators';
import { useFinancialEntitiesContext } from '@common/react/financialEntities';
import { useCurrentUser } from '@common/react/user';
import { useState } from 'react';
import Modal from '../common/modals/Modal';
import ModalManager, { useModal } from '../ModalManager';
import ConnectionTypeView from './ConnectionTypeView';
import ManualAddSharingView from './ManualAddSharingView';
import ManualAddTypeView from './ManualAddTypeView';
import ManualAddView from './ManualAddView';

type Step =
    | 'ConnectionType'
    | 'ManualAddType'
    | 'ManualAddForm'
    | 'ManualAddSharing'
    | 'ConnectAccountInstitutionList';

export interface FinancialEntityAddFlowProps {
    isInRetirementContext?: boolean;
    onBackFirstStep?: VoidFunction;
    onCompleteConnection?: VoidFunction;
    onCompleteManual?: (feId: string) => void | Promise<void>;
    initialStep?: Step;
}

export const startFinancialEntityAddFlow = (
    props: FinancialEntityAddFlowProps
) => ModalManager.show(FinancialEntityAddFlowModal, props);

export const hideFinancialEntityAddFlow = () =>
    ModalManager.hide(FinancialEntityAddFlowModal);

const FinancialEntityAddFlowNavigator = ({
    isInRetirementContext = false,
    onBackFirstStep,
    onCompleteConnection,
    onCompleteManual,
    initialStep = 'ConnectionType',
}: FinancialEntityAddFlowProps) => {
    useTrackModalShown('Financial Entity Add Flow');

    const modal = useModal();
    const types = isInRetirementContext
        ? ASSET_TYPES
        : financialCategoryConfigs['All'].types;
    const [stepStack, setStepStack] = useState<Step[]>([initialStep]);
    const [financialEntityType, setFinancialEntityType] =
        useState<ExternalFinancialEntityType>();
    const [financialEntityId, setFinancialEntityId] = useState<string>();
    const { activePartnershipId } = useCurrentUser();
    const { fes } = useFinancialEntitiesContext();

    const setStep = (newStep: Step) => {
        setStepStack((prev) => (prev ? [...prev, newStep] : [newStep]));
    };

    const close = () => {
        setStepStack([initialStep]);
        setFinancialEntityType(undefined);
        setFinancialEntityId(undefined);
        modal.hide();
    };

    const usePlaidOptions: UsePlaidOptions = {
        onComplete: onCompleteConnection,
        // When connecting accounts as part of Included Accounts for Retirement,
        // show "Continue" at the end when the "Which accounts" modal appears
        confirmationNextButtonText: isInRetirementContext
            ? 'Continue'
            : undefined,
    };

    const { open } = usePlaid(usePlaidOptions);

    const modalProps = {
        size: 'lg',
        isCentered: true,
        isOpen: modal.visible,
        onClose: close,
    };

    const handleBack = () => {
        if (stepStack.length) {
            setStepStack((currentStepStack) => {
                currentStepStack.pop();
                return [...currentStepStack];
            });
        } else {
            close();
        }
    };

    const currentStep = stepStack[stepStack.length - 1];

    const handleConnectAccount = () => {
        // Shouldn't be possible to get here without financial entities first loading
        assert(fes.data);

        const myConnectedFEs = fes.data.financialEntities.filter(
            (fe) => fe.isMine && fe.linkStatus !== 'MANUAL'
        );

        if (myConnectedFEs.length === 0) {
            // No FEs yet, or all FEs were added manually - open Plaid directly
            open();
            close();
        } else {
            // At least one account is connected - show the list of institutions
            setStep('ConnectAccountInstitutionList');
        }
    };

    switch (currentStep) {
        case 'ConnectionType': {
            return (
                <Modal {...modalProps} onBack={onBackFirstStep}>
                    <ConnectionTypeView
                        onAddManually={() => setStep('ManualAddType')}
                        onConnectAccount={handleConnectAccount}
                        isInRetirementContext={isInRetirementContext}
                    />
                </Modal>
            );
        }
        case 'ConnectAccountInstitutionList': {
            return (
                <Modal {...modalProps} onBack={handleBack} size="lg">
                    <ConnectAccountInstitutionListView
                        plaidOptions={usePlaidOptions}
                        showExistingInstitutions={
                            initialStep === 'ConnectAccountInstitutionList'
                        }
                    />
                </Modal>
            );
        }
        case 'ManualAddType': {
            return (
                <Modal {...modalProps} onBack={handleBack}>
                    <ManualAddTypeView
                        title="Add an account manually"
                        types={types}
                        onSelectType={(type) => {
                            setFinancialEntityType(type);
                            setStep('ManualAddForm');
                        }}
                    />
                </Modal>
            );
        }
        case 'ManualAddForm': {
            assert(
                financialEntityType,
                'financialEntityType must be selected to show form'
            );
            const shouldShowSharingStepOnComplete =
                !!activePartnershipId && !isInRetirementContext;

            return (
                <Modal {...modalProps} onBack={handleBack}>
                    <ManualAddView
                        type={financialEntityType}
                        nextButtonText={
                            shouldShowSharingStepOnComplete
                                ? 'Continue'
                                : 'Looks good'
                        }
                        onCancel={handleBack}
                        onConnectAccount={handleConnectAccount}
                        onComplete={async (fe) => {
                            if (shouldShowSharingStepOnComplete) {
                                setFinancialEntityId(fe.id);
                                setStep('ManualAddSharing');
                            } else {
                                await onCompleteManual?.(fe.id);
                                setTimeout(() => {
                                    showConfetti({
                                        origin: { y: 0.6, x: 0.8 },
                                    });
                                }, 500);
                                close();
                            }
                        }}
                    />
                </Modal>
            );
        }
        case 'ManualAddSharing': {
            assert(
                activePartnershipId,
                'partnership ID must exist to show form'
            );
            return (
                <Modal {...modalProps}>
                    <ManualAddSharingView
                        activePartnershipId={activePartnershipId}
                        financialEntityId={financialEntityId}
                        onNext={async () => {
                            assert(financialEntityId, 'FE ID must exist');
                            await onCompleteManual?.(financialEntityId);
                            setTimeout(() => {
                                showConfetti({ origin: { y: 0.6, x: 0.8 } });
                            }, 500);
                            close();
                        }}
                    />
                </Modal>
            );
        }
    }
};

const FinancialEntityAddFlowModal =
    ModalManager.create<FinancialEntityAddFlowProps>(
        FinancialEntityAddFlowNavigator
    );
